import { LayoutDashboard, Heart, MessageSquare, FileText, Trash2, Eye, Plus, Send, Clock, CreditCard, BadgeCheck, RotateCcw, Bookmark, Receipt, Search as SearchIcon, X, UserCheck, Ban, Bell, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ListingCard } from "@/components/ListingCard";
import { formatPrice, resolveImageUrl } from "@/lib/api";

export const SEARCH_LABELS = { q: "Keyword", category: "Category", subcategory: "Sub-category", county: "County", area: "Area", condition: "Condition", min_price: "Min €", max_price: "Max €", min_bedrooms: "Min beds" };

export const DASHBOARD_TABS = [
  { k: "ads", label: "My Ads", icon: LayoutDashboard },
  { k: "saved", label: "Saved Ads", icon: Heart },
  { k: "searches", label: "Saved Searches", icon: Bookmark },
  { k: "following", label: "Following", icon: UserCheck },
  { k: "messages", label: "Messages", icon: MessageSquare },
  { k: "wallet", label: "Credit Wallet", icon: Wallet },
  { k: "payments", label: "Payment History", icon: Receipt },
  { k: "notifications", label: "Notifications", icon: Bell },
  { k: "blocked", label: "Blocked Users", icon: Ban },
];

export const walletBonus = (amount, tiers) => {
  if (!amount || !Array.isArray(tiers)) return 0;
  const hit = tiers.filter((t) => amount >= t.min).sort((a, b) => b.min - a.min)[0];
  return hit ? hit.bonus : 0;
};

function ManageRow({ l, onDelete, navigate, draft, onMarkSold, onRelist }) {
  const img = resolveImageUrl((l.images && l.images[0]) || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&q=80");
  const isSold = l.status === "sold";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`manage-row-${l.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <img src={img} alt={l.title} className={`w-16 h-16 rounded object-cover shrink-0 ${isSold ? "opacity-60" : ""}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate flex items-center gap-2">{l.title}
            {isSold && <span className="text-xs bg-slate-200 text-slate-600 rounded px-1.5 py-0.5">Sold</span>}
          </p>
          <p className="text-sm text-emerald-700 font-semibold">{formatPrice(l.price, l.category)}</p>
          <p className="text-xs text-slate-400 flex items-center gap-1"><Eye size={12} /> {l.views} views · {l.county}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 sm:justify-end">
        {!draft && !isSold && <Button size="sm" variant="outline" data-testid={`view-${l.id}`} onClick={() => navigate(`/listing/${l.id}`)}>View</Button>}
        <Button size="sm" variant="outline" data-testid={`edit-${l.id}`} onClick={() => navigate(`/place-ad?edit=${l.id}`)}>Edit</Button>
        {l.status === "active" && <Button size="sm" variant="outline" data-testid={`mark-sold-${l.id}`} onClick={() => onMarkSold(l.id)} className="text-emerald-700 border-emerald-200"><BadgeCheck size={15} className="mr-1" /> Mark sold</Button>}
        {isSold && <Button size="sm" variant="outline" data-testid={`relist-${l.id}`} onClick={() => onRelist(l.id)} className="text-emerald-700 border-emerald-200"><RotateCcw size={15} className="mr-1" /> Relist</Button>}
        <Button size="sm" variant="ghost" data-testid={`delete-${l.id}`} onClick={() => onDelete(l.id)} className="text-red-600"><Trash2 size={16} /></Button>
      </div>
    </div>
  );
}

export function AdsTab({ ctx }) {
  const { pending, active, sold, drafts, del, navigate, markSold, relist, completePayment } = ctx;
  return (
    <div className="space-y-6" data-testid="tab-content-ads">
      {pending.length > 0 && (
        <section data-testid="pending-payment-section">
          <h2 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><Clock size={16} className="text-amber-500" /> Awaiting payment ({pending.length})</h2>
          <div className="space-y-3">
            {pending.map((l) => (
              <div key={l.id} className="bg-white border border-amber-200 rounded-lg p-3 flex items-center gap-4" data-testid={`pending-row-${l.id}`}>
                <img src={resolveImageUrl((l.images && l.images[0]) || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&q=80")} alt={l.title} className="w-16 h-16 rounded object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{l.title}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mt-1">Payment pending{l.premium ? " · Spotlight" : ""}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" data-testid={`complete-payment-${l.id}`} onClick={() => completePayment(l.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CreditCard size={16} className="mr-1" /> Complete payment</Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-${l.id}`} onClick={() => del(l.id)} className="text-red-600"><Trash2 size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <section>
        <h2 className="font-heading font-semibold text-slate-900 mb-3">Active listings ({active.length})</h2>
        {active.length === 0 ? <p className="text-slate-400 text-sm">Nothing live just yet. Pop an ad up and it'll show here.</p> : (
          <div className="space-y-3">
            {active.map((l) => <ManageRow key={l.id} l={l} onDelete={del} navigate={navigate} onMarkSold={markSold} />)}
          </div>
        )}
      </section>
      {sold.length > 0 && (
        <section data-testid="sold-section">
          <h2 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><BadgeCheck size={16} className="text-slate-500" /> Sold ({sold.length})</h2>
          <div className="space-y-3">{sold.map((l) => <ManageRow key={l.id} l={l} onDelete={del} navigate={navigate} onRelist={relist} />)}</div>
        </section>
      )}
      {drafts.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><FileText size={16} /> Drafts ({drafts.length})</h2>
          <div className="space-y-3">{drafts.map((l) => <ManageRow key={l.id} l={l} onDelete={del} navigate={navigate} draft />)}</div>
        </section>
      )}
    </div>
  );
}

export function SavedTab({ ctx }) {
  const { saved } = ctx;
  return (
    <div data-testid="tab-content-saved">
      {saved.length === 0 ? <p className="text-slate-400 text-sm">You haven't saved any ads yet.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{saved.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
      )}
    </div>
  );
}

export function MessagesTab({ ctx }) {
  const { convos, activeConvo, setActiveConvo, reply, setReply, sendReply } = ctx;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border border-slate-200 rounded-lg overflow-hidden min-h-[420px]" data-testid="tab-content-messages">
      <div className="md:col-span-1 border-r border-slate-200 max-h-[500px] overflow-y-auto">
        {convos.length === 0 ? <p className="text-slate-400 text-sm p-4">No conversations.</p> : convos.map((c) => (
          <button key={c.convo_key} data-testid={`convo-${c.convo_key}`} onClick={() => setActiveConvo(c)}
            className={`w-full text-left p-3 border-b border-slate-100 flex gap-2 items-center ${activeConvo?.convo_key === c.convo_key ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
            {c.listing_image && <img src={resolveImageUrl(c.listing_image)} alt={c.listing_title || "Listing"} className="w-10 h-10 rounded object-cover" />}
            <div className="min-w-0"><p className="text-sm font-medium truncate">{c.other_name}</p><p className="text-xs text-slate-500 truncate">{c.listing_title}</p></div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {activeConvo ? (
          <>
            <div className="p-3 border-b border-slate-200 text-sm font-medium">{activeConvo.listing_title}</div>
            <div className="flex-1 p-4 space-y-2 overflow-y-auto max-h-[360px]">
              {activeConvo.messages.map((m) => (
                <div key={`${m.created_at}-${m.sender_id}`} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.mine ? "ml-auto bg-emerald-600 text-white" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
              ))}
            </div>
            <div className="p-3 border-t border-slate-200 flex gap-2">
              <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Type a reply..." data-testid="reply-input" />
              <Button onClick={sendReply} data-testid="send-reply" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Send size={16} /></Button>
            </div>
          </>
        ) : <div className="flex-1 grid place-items-center text-slate-400 text-sm">Select a conversation</div>}
      </div>
    </div>
  );
}

export function SearchesTab({ ctx }) {
  const { searches, runSearch, deleteSearch } = ctx;
  return (
    <div data-testid="tab-content-searches" className="space-y-3">
      {searches.length === 0 ? (
        <p className="text-slate-400 text-sm">No saved searches yet. On the search page, set your filters and tap <span className="font-medium">"Save this search"</span>.</p>
      ) : searches.map((s) => (
        <div key={s.id} data-testid={`saved-search-${s.id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 shrink-0"><SearchIcon size={18} /></span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{s.name}</p>
            <p className="text-xs text-slate-500 truncate">{Object.entries(s.query).filter(([k]) => k !== "sort").map(([k, v]) => `${SEARCH_LABELS[k] || k}: ${v}`).join(" · ") || "All ads"}</p>
          </div>
          <Button size="sm" data-testid={`run-search-${s.id}`} onClick={() => runSearch(s.query)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Run</Button>
          <Button size="sm" variant="ghost" data-testid={`delete-search-${s.id}`} onClick={() => deleteSearch(s.id)} className="text-red-600"><X size={16} /></Button>
        </div>
      ))}
    </div>
  );
}

export function PaymentsTab({ ctx }) {
  const { payments } = ctx;
  return (
    <div data-testid="tab-content-payments" className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {payments.length === 0 ? (
        <p className="text-slate-400 text-sm p-4">No payments yet. Listing fees and Spotlight boosts will show up here.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {payments.map((p) => (
            <div key={p.id} data-testid={`payment-row-${p.id}`} className="p-3 flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-md bg-slate-100 text-slate-500 shrink-0"><Receipt size={18} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{p.listing_title}</p>
                <p className="text-xs text-slate-500">
                  {p.purpose === "free_first_ad" ? "First ad (free)" : p.spotlight ? "Listing fee + Spotlight" : "Listing fee"}
                  {p.created_at ? ` · ${new Date(p.created_at).toLocaleDateString()}` : ""}
                </p>
              </div>
              <span className={`text-xs rounded px-2 py-0.5 shrink-0 ${p.status === "complete" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{p.status === "complete" ? "Paid" : p.status}</span>
              <p className="font-semibold text-slate-900 w-16 text-right shrink-0">{p.amount > 0 ? `€${p.amount.toFixed(2)}` : "FREE"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FollowingTab({ ctx }) {
  const { following, runSearch, unfollow } = ctx;
  return (
    <div data-testid="tab-content-following" className="space-y-3">
      {following.length === 0 ? (
        <p className="text-slate-400 text-sm">You're not following any sellers yet. Tap <span className="font-medium">Follow</span> on a seller's ad to keep an eye on their listings.</p>
      ) : following.map((f) => (
        <div key={f.seller_id} data-testid={`following-${f.seller_id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 shrink-0 font-semibold">{(f.seller_name || "S")[0]}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{f.seller_name}</p>
            <p className="text-xs text-slate-500">{f.active_ads} active {f.active_ads === 1 ? "ad" : "ads"}</p>
          </div>
          <Button size="sm" data-testid={`view-seller-ads-${f.seller_id}`} onClick={() => runSearch({ seller_id: f.seller_id })} className="bg-emerald-600 hover:bg-emerald-700 text-white">View ads</Button>
          <Button size="sm" variant="ghost" data-testid={`unfollow-${f.seller_id}`} onClick={() => unfollow(f.seller_id)} className="text-red-600"><X size={16} /></Button>
        </div>
      ))}
    </div>
  );
}

export function WalletTab({ ctx }) {
  const { wallet, topupAmt, setTopupAmt, topup } = ctx;
  return (
    <div data-testid="tab-content-wallet" className="space-y-4">
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-6">
        <p className="text-sm text-emerald-100">Credit balance</p>
        <p className="font-heading text-4xl font-bold mt-1" data-testid="wallet-balance">€{(wallet?.balance ?? 0).toFixed(2)}</p>
        <p className="text-xs text-emerald-100 mt-2">Use credits to pay listing fees and Spotlight boosts instantly. No card needed at checkout.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="font-heading font-semibold text-slate-900 mb-1">Top up your wallet</p>
        <p className="text-xs text-emerald-700 mb-3">Bonus credit: top up €20 get €1 free · €50 get €5 free · €100 get €15 free.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {["10", "20", "50", "100"].map((a) => {
            const bonus = walletBonus(Number(a), wallet?.bonus_tiers);
            return (
              <button key={a} data-testid={`topup-preset-${a}`} onClick={() => setTopupAmt(a)}
                className={`relative px-3 py-1.5 rounded-md border text-sm font-medium ${topupAmt === a ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
                €{a}
                {bonus > 0 && <span className="ml-1 text-[11px] font-semibold text-amber-600">+€{bonus}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 items-center">
          <Input type="number" min="5" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)} data-testid="topup-amount" className="max-w-[140px]" />
          <Button onClick={topup} data-testid="topup-button" className="bg-amber-500 hover:bg-amber-600 text-white"><CreditCard size={16} className="mr-1" /> Top up</Button>
        </div>
        {walletBonus(Number(topupAmt), wallet?.bonus_tiers) > 0 && (
          <p className="text-xs text-emerald-700 mt-2" data-testid="topup-bonus-note">
            You'll get <span className="font-semibold">€{walletBonus(Number(topupAmt), wallet?.bonus_tiers).toFixed(2)} free</span>, €{(Number(topupAmt) + walletBonus(Number(topupAmt), wallet?.bonus_tiers)).toFixed(2)} total credit.
          </p>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200"><p className="font-heading font-semibold text-slate-900">Wallet activity</p></div>
        {(!wallet || wallet.transactions.length === 0) ? (
          <p className="text-slate-400 text-sm p-4">No wallet activity yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {wallet.transactions.map((t) => (
              <div key={t.id} data-testid={`wallet-txn-${t.id}`} className="p-3 flex items-center gap-3">
                <span className={`grid place-items-center w-9 h-9 rounded-md shrink-0 ${t.amount >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{t.amount >= 0 ? <Plus size={16} /> : <Receipt size={16} />}</span>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{t.description}</p><p className="text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</p></div>
                <p className={`font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-slate-700"}`}>{t.amount >= 0 ? "+" : "−"}€{Math.abs(t.amount).toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function NotificationsTab({ ctx }) {
  const { notif, saveNotif } = ctx;
  return (
    <div data-testid="tab-content-notifications" className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
      {[
        { k: "new_message", t: "New messages", d: "Email me when a buyer or seller sends me a message" },
        { k: "saved_search", t: "Saved search matches", d: "Email me when new ads match one of my saved searches" },
        { k: "marketing", t: "Tips & offers", d: "Occasional DealShed news, tips and promotions" },
      ].map((row) => (
        <div key={row.k} className="p-4 flex items-center justify-between gap-4">
          <div><p className="font-medium text-slate-900">{row.t}</p><p className="text-sm text-slate-500">{row.d}</p></div>
          <Switch data-testid={`notif-${row.k}`} checked={!!notif?.[row.k]} onCheckedChange={(v) => saveNotif(row.k, v)} />
        </div>
      ))}
    </div>
  );
}

export function BlockedTab({ ctx }) {
  const { blocked, unblock } = ctx;
  return (
    <div data-testid="tab-content-blocked" className="space-y-3">
      {blocked.length === 0 ? (
        <p className="text-slate-400 text-sm">You haven't blocked anyone. You can block a seller from their ad page.</p>
      ) : blocked.map((b) => (
        <div key={b.user_id} data-testid={`blocked-${b.user_id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-red-50 text-red-500 shrink-0"><Ban size={18} /></span>
          <p className="flex-1 min-w-0 font-medium text-slate-900 truncate">{b.name}</p>
          <Button size="sm" variant="outline" data-testid={`unblock-${b.user_id}`} onClick={() => unblock(b.user_id)}>Unblock</Button>
        </div>
      ))}
    </div>
  );
}

export const DASHBOARD_PANELS = {
  ads: AdsTab, saved: SavedTab, searches: SearchesTab, following: FollowingTab,
  messages: MessagesTab, wallet: WalletTab, payments: PaymentsTab,
  notifications: NotificationsTab, blocked: BlockedTab,
};
